/**
 * Reads the same public, unauthenticated release manifest the desktop app's
 * in-app updater polls — see `apps/desktop/src-tauri/tauri.conf.json`'s
 * `plugins.updater.endpoints` and `apps/desktop/scripts/make-update-manifest.mjs`,
 * which is what writes `latest.json` at release time.
 *
 * No backend involved: the manifest and installers live in a public R2
 * bucket (the repository itself is private, so GitHub Releases can't be
 * linked from here), and this is a plain browser `fetch`.
 */

/** The public R2 bucket every published file is served from. */
const RELEASES_ORIGIN = "https://releases.bigbearbackup.com";

/** Mirrors the R2 bucket's `desktop/` prefix — kept in one place because the
 * updater endpoint, the NSIS/MSI download links, and the manifest URL below
 * all derive from it. Must match the base URL baked into `tauri.conf.json`
 * and passed to `make-update-manifest.mjs` in `.github/workflows/desktop.yml`. */
const R2_BASE_URL = `${RELEASES_ORIGIN}/desktop`;

/** The bucket's `cli/` prefix, beside `desktop/`: the Linux command line's
 * `<version>/` folders and `latest.json`, as `.github/workflows/cli-linux.yml`
 * packs them and desktop.yml's release mirrors them (#958). */
const CLI_BASE_URL = `${RELEASES_ORIGIN}/cli`;

/** The manifest's on-the-wire shape — snake_case, since it's written by
 * `tauri-plugin-updater`'s own conventions, not by this codebase. */
interface RawManifest {
  version: string;
  notes: string;
  pub_date: string;
  platforms: {
    "windows-x86_64": { signature: string; url: string };
  };
  /** Absent on manifests published before checksums were added. */
  checksums?: { nsis_sha256: string; msi_sha256: string };
}

export interface LatestRelease {
  version: string;
  notes: string;
  pubDate: string;
  /** The NSIS installer — what the in-app updater itself installs, and the
   * recommended download for anyone getting the app for the first time. */
  nsisUrl: string;
  /** The MSI — never an updater target (see the release plan for why), but
   * published alongside it for Programs & Features / Group Policy /
   * Intune-style installs. Only `latest.json`'s NSIS entry is authoritative,
   * so this is derived from the same fixed naming convention
   * `make-update-manifest.mjs` stages installers under, not read from the
   * manifest itself. */
  msiUrl: string;
  /** SHA-256 of each installer, hex-encoded, for anyone verifying the
   * download independently of the link itself. Absent on releases published
   * before checksums were added to the manifest. */
  nsisSha256?: string;
  msiSha256?: string;
}

export async function fetchLatestRelease(
  signal?: AbortSignal,
): Promise<LatestRelease> {
  // `exactOptionalPropertyTypes` rejects passing `signal: undefined`, so an
  // absent signal means omitting the key rather than setting it.
  const response = await fetch(
    `${R2_BASE_URL}/latest.json`,
    signal ? { signal } : {},
  );
  if (!response.ok) {
    throw new Error(
      `Could not reach the release manifest (HTTP ${response.status}).`,
    );
  }
  const manifest = (await response.json()) as RawManifest;
  return {
    version: manifest.version,
    notes: manifest.notes,
    pubDate: manifest.pub_date,
    nsisUrl: manifest.platforms["windows-x86_64"].url,
    msiUrl: `${R2_BASE_URL}/Big-Bear-Backup_${manifest.version}_x64.msi`,
    ...(manifest.checksums
      ? {
          nsisSha256: manifest.checksums.nsis_sha256,
          msiSha256: manifest.checksums.msi_sha256,
        }
      : {}),
  };
}

/**
 * Same fixed naming convention `make-update-manifest.mjs` stages installers
 * under, so a page can link to a specific version's assets — including one
 * `releases/*.md` names that `latest.json` doesn't (yet, or any longer)
 * agree with — without waiting on a manifest fetch.
 */
export function downloadUrlsFor(version: string): {
  nsisUrl: string;
  msiUrl: string;
} {
  const base = `${R2_BASE_URL}/Big-Bear-Backup_${version}_x64`;
  return { nsisUrl: `${base}-setup.exe`, msiUrl: `${base}.msi` };
}

/** The Linux command line's builds, in the order the Download page lists
 * them, each by the Rust target its tarball is named after. */
const CLI_TARGETS = [
  { target: "x86_64-unknown-linux-musl", label: "x86_64 (Intel and AMD)" },
  { target: "aarch64-unknown-linux-musl", label: "aarch64 (ARM)" },
] as const;

export interface CliBuild {
  readonly target: (typeof CLI_TARGETS)[number]["target"];
  readonly label: string;
  /** The tarball's file name, `bigbear-<version>-<target>.tar.gz`. */
  readonly name: string;
  readonly url: string;
  /** Only known once the live manifest has responded. */
  readonly sha256?: string;
}

export interface CliDownloads {
  readonly version: string;
  /** The version's folder, `cli/<version>`, which holds every file below. */
  readonly folderUrl: string;
  readonly builds: readonly CliBuild[];
  /** `SHA256SUMS`, covering every build of the version. */
  readonly sumsUrl: string;
}

/** `cli/latest.json` on the wire, as cli-linux.yml writes it. */
interface RawCliManifest {
  version: string;
  pub_date: string;
  files: { target: string; name: string; sha256: string }[];
}

/**
 * The same fixed naming the release packs the tarballs under, so a page can
 * link a version's builds without waiting on the manifest.
 */
export function cliDownloadsFor(version: string): CliDownloads {
  const base = `${CLI_BASE_URL}/${version}`;
  return {
    version,
    folderUrl: base,
    builds: CLI_TARGETS.map(({ target, label }) => {
      const name = `bigbear-${version}-${target}.tar.gz`;
      return { target, label, name, url: `${base}/${name}` };
    }),
    sumsUrl: `${base}/SHA256SUMS`,
  };
}

/** The newest Linux command line, from the manifest the release writes last,
 * once every file it names is published; `null` while there is no manifest
 * at all, before the first release that publishes one. */
export async function fetchLatestCliRelease(signal?: AbortSignal): Promise<CliDownloads | null> {
  const response = await fetch(`${CLI_BASE_URL}/latest.json`, signal ? { signal } : {});
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Could not reach the command line's release manifest (HTTP ${response.status}).`);
  }
  const manifest = (await response.json()) as RawCliManifest;
  const downloads = cliDownloadsFor(manifest.version);
  return {
    ...downloads,
    builds: downloads.builds.map((build) => {
      const file = manifest.files.find((f) => f.target === build.target);
      if (!file) throw new Error(`The manifest names no ${build.target} build.`);
      return { ...build, sha256: file.sha256 };
    }),
  };
}
